/** At most this many read columns render; the rest are reached through the sample picker. */
export const SAMPLE_COLUMN_LIMIT = 300

/** The shortest prefix the read columns' headers are shortened by. */
export const MIN_COMMON_PREFIX = 6

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * A read column's key as the URL keeps it: a sample uuid by its first eight digits, so that a few hundred picked
 * samples stay well inside a URL's length limits; any other key whole.
 */
export const compactColumnKey = (key: string): string => (UUID.test(key) ? key.slice(0, 8) : key)

export type SampleColumnWindow<C> = {
  shown: C[]
  total: number
  /** Whether there are more columns than may render, which is when the picker shows. */
  windowed: boolean
}

/**
 * The read columns that render: all of them up to [limit]; past it the picked ones ([selection], compact keys) in
 * column order, or the first [limit] when nothing is picked or no pick names a column any more, as after a regrouping.
 */
export function sampleColumnWindow<C extends { key: string }>(
  columns: C[],
  selection: readonly string[] | null,
  limit: number = SAMPLE_COLUMN_LIMIT
): SampleColumnWindow<C> {
  const total = columns.length
  if (total <= limit) return { shown: columns, total, windowed: false }
  if (selection !== null) {
    const picked = new Set(selection)
    const shown = columns.filter((c) => picked.has(compactColumnKey(c.key)))
    if (shown.length > 0 || selection.length === 0) return { shown: shown.slice(0, limit), total, windowed: true }
  }
  return { shown: columns.slice(0, limit), total, windowed: true }
}

/** The picks that show the [shown] columns with [key]'s column added or removed. */
export function toggleColumnPick(shown: { key: string }[], key: string): string[] {
  const picks = shown.map((c) => compactColumnKey(c.key))
  const token = compactColumnKey(key)
  return picks.includes(token) ? picks.filter((t) => t !== token) : [...picks, token]
}

/** The `readsSamples` URL value: the picked keys, each URI-encoded so a key may hold a comma. */
export const writeColumnPicks = (selection: readonly string[]): string => selection.map(encodeURIComponent).join(',')

export function parseColumnPicks(value: string | null): string[] | null {
  if (value === null) return null
  if (value === '') return []
  try {
    return value.split(',').map(decodeURIComponent)
  } catch {
    return null
  }
}

/** Columns whose label contains [query], case-insensitively. */
export const searchColumns = <C extends { label: string }>(columns: C[], query: string): C[] => {
  const q = query.trim().toLowerCase()
  return q === '' ? columns : columns.filter((c) => c.label.toLowerCase().includes(q))
}

/**
 * The prefix all [labels] share, when showing the labels without it is worth it: it is at least
 * {@link MIN_COMMON_PREFIX} characters long and no label is the prefix alone; otherwise null.
 */
export function commonLabelPrefix(labels: readonly string[]): string | null {
  if (labels.length === 0) return null
  let prefix = labels[0]!
  for (const label of labels) {
    while (!label.startsWith(prefix)) prefix = prefix.slice(0, -1)
    if (prefix.length < MIN_COMMON_PREFIX) return null
  }
  return labels.some((label) => label.length === prefix.length) ? null : prefix
}
